import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./components/ThemeProvider";
import SupportButton from "./components/SupportButton";
import Footer from "./components/Footer";

// Pages
import Login from "./pages/Login";
import Setup2FA from "./pages/Setup2FA";
import Dashboard from "./pages/Dashboard";
import MacroAreaView from "./pages/MacroAreaView";
import OrderDetail from "./pages/OrderDetail";
import NewOrder from "./pages/NewOrder";
import ASLMandates from "./pages/ASLMandates";
import Collaborators from "./pages/Collaborators";
import WalletPage from "./pages/WalletPage";
import MarketingMaterials from "./pages/MarketingMaterials";
import Clients from "./pages/Clients";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  
  useEffect(() => {
    if (location.state?.user) return;
    
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/login");
      }
    };
    
    checkAuth();
  }, [navigate, location.state]);
  
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (isAuthenticated === false) {
    return null;
  }
  
  return (
    <>
      {children({ user, setUser })}
      <SupportButton />
      <Footer />
    </>
  );
};

// App Router Component
function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/setup-2fa" element={<Setup2FA />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          {({ user }) => <Dashboard user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/area/:macroArea" element={
        <ProtectedRoute>
          {({ user }) => <MacroAreaView user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/orders/new" element={
        <ProtectedRoute>
          {({ user }) => <NewOrder user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/orders/:orderId" element={
        <ProtectedRoute>
          {({ user }) => <OrderDetail user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/orders/:orderId/edit" element={
        <ProtectedRoute>
          {({ user }) => <NewOrder user={user} isEdit />}
        </ProtectedRoute>
      } />
      <Route path="/asl-mandates" element={
        <ProtectedRoute>
          {({ user }) => <ASLMandates user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/collaborators" element={
        <ProtectedRoute>
          {({ user }) => <Collaborators user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/wallet" element={
        <ProtectedRoute>
          {({ user }) => <WalletPage user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/marketing" element={
        <ProtectedRoute>
          {({ user }) => <MarketingMaterials user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/clients" element={
        <ProtectedRoute>
          {({ user }) => <Clients user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>
          <div className="App min-h-screen bg-background text-foreground transition-colors duration-300">
            <AppRouter />
            <Toaster 
              position="top-right" 
              richColors 
              closeButton
              toastOptions={{
                className: 'font-sans'
              }}
            />
          </div>
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export { API, BACKEND_URL };
export default App;
